import { EXTERNAL_LINKS, useAnalytics } from '@psyomics/ui-common';
import { Button, LoadingSpinner, PageLayout, Puck } from '@psyomics/ui-components';
import { useEffect, useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import { ContentType, OrganisationContent, ReferralInvite } from '../../../../features/referral/generated/referral';
import { useReferralInviteContentQuery } from '../../../../features/referral/hooks/use-referral-invite-query';
import { usePatientAppBarContext } from '../../../../shared/contexts/patient-app-bar';

const getNextRoute = (invite: ReferralInvite) => {
  const redirectToSurvey =
    invite.status === 'pending' &&
    invite.referralSession.status === 'pending' &&
    invite.referralSession.referral.status === 'pending';
  const redirect = redirectToSurvey ? `/questionnaire/${invite.publicId}` : '/';
  const params = new URLSearchParams({
    invitePublicId: invite.publicId,
    redirect,
  });

  return invite.hasExistingAccount ? `/login?${params.toString()}` : `/register?${params.toString()}`;
};

const getContentPageMetadata = (invite: ReferralInvite, inviteeName: string, organisationName: string) => {
  const { organisation } = invite.referralSession.referral;
  return {
    givenName: inviteeName,
    orgName: organisationName,
    orgNameAbbreviation: organisation?.shortName ?? undefined,
    orgPrivacyPolicyUrl: organisation?.privacyPolicyUrl ?? undefined,
    psyomicsPrivacyPolicyUrl: EXTERNAL_LINKS.PRIVACY_POLICY,
  };
};

const getContentOrThrow = (orgContent: OrganisationContent, inviteId: string) => {
  try {
    return JSON.parse(orgContent.content.definition);
  } catch {
    throw new Error('landing page content definition is not valid JSON for invite: ' + inviteId);
  }
};

const useInviteParams = (invite: ReferralInvite) =>
  useMemo(() => {
    const {
      givenName: inviteeName,
      referralSession: {
        referral: { organisation },
      },
    } = invite;
    const organisationName = organisation?.name;
    const organisationCode = organisation?.code;

    return {
      inviteeName,
      organisationCode,
      organisationName,
    };
  }, [invite]);

const Welcome = ({ invite }: { readonly invite: ReferralInvite }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const { setWithIllustration } = usePatientAppBarContext();
  const { inviteeName, organisationCode, organisationName } = useInviteParams(invite);
  const [organisationContent, fetching, error] = useReferralInviteContentQuery(
    ContentType.LandingPage,
    invite.publicId
  );

  useLayoutEffect(() => {
    setWithIllustration(true);
    return () => {
      setWithIllustration(false);
    };
  }, [setWithIllustration]);

  useEffect(() => {
    if (organisationCode) {
      analytics.setGroup({
        groupType: 'organisation',
        groupKey: organisationCode,
      });
    }
  }, [organisationCode, analytics]);

  if (fetching) return <LoadingSpinner />;
  if (error) throw error;
  if (!organisationContent) throw new Error('landing page content not defined for invite: ' + invite.id);
  if (!inviteeName || !organisationName) return <Navigate to="/" />;

  const next = getNextRoute(invite);

  return (
    <PageLayout className="break-words">
      <div data-testid="referral-welcome">
        <Puck.ContentPage.Render
          metadata={getContentPageMetadata(invite, inviteeName, organisationName)}
          reportDefinition={getContentOrThrow(organisationContent, invite.id)}
        />
        <div className="mt-5.5">
          <Button className="w-full" onClick={() => navigate(next)}>
            {t('page.viewReferral.nextButton')}
          </Button>
        </div>
      </div>
    </PageLayout>
  );
};

export { getNextRoute, Welcome };
